import { Component, EventEmitter, Input, Output, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { MessageService } from 'primeng/api'
import { catchError, of } from 'rxjs'

import { AuthService } from '@data/services/modules/auth/auth.service'
import { environment } from '@env/environment'

export type LeaseSituation = 'NORMAL' | 'EN_JUICIO' | 'REESTRUCTURACION' | 'CARTERA_VENCIDA'

interface Opcion {
  value: LeaseSituation
  label: string
  help: string
}

/**
 * Las cuatro situaciones, con lo que significa cada una para la cartera.
 *
 * El texto de ayuda no es decoración: marcar EN JUICIO **baja el total de la cartera del mes**, y
 * quien lo hace tiene que saberlo antes de darle a Guardar, no después de que Finanzas pregunte por
 * qué la cifra cambió.
 */
const OPCIONES: Opcion[] = [
  { value: 'NORMAL', label: 'Normal', help: 'El arrendamiento se cobra por la vía de siempre y cuenta en la cartera.' },
  {
    value: 'EN_JUICIO',
    label: 'En juicio',
    help: 'La cobranza pasó a la vía judicial. SALE de la cartera, del avance del mes y del reporte de mora — su mora sigue devengándose, solo deja de aparecer en esas listas.',
  },
  { value: 'REESTRUCTURACION', label: 'Reestructuración', help: 'Se renegoció el contrato. SIGUE contando en la cartera: se renegoció, no se dejó de cobrar.' },
  {
    value: 'CARTERA_VENCIDA',
    label: 'Cartera vencida',
    help: 'Se decidió dejar de contarlo como cobrable. SALE de la cartera, del avance del mes y del reporte de mora — su saldo sigue registrado y se puede volver a Normal.',
  },
]

/**
 * La situación del arrendamiento, en el encabezado y junto a la cartera.
 *
 * ## Por qué es una marca y no un estado
 *
 * `status` describe dónde va el contrato en su ciclo —Pendiente, Aprobado, Activo, Liquidado,
 * Cancelado—. EN JUICIO y REESTRUCTURACIÓN no son etapas de ese ciclo: un arrendamiento en juicio
 * **sigue activo**, y uno reestructurado también. El razonamiento completo, con las 84 consultas de
 * la API que filtran por `status: 'Activo'` y que dejarían de ver estos arrendamientos si fueran
 * estados, está en `lease-situation.enum.ts` del backend.
 *
 * Se pinta siempre, incluso en Normal, por lo mismo que la píldora de cartera dice «Sin asignar»:
 * un control que solo existe cuando ya está puesto no se puede poner la primera vez.
 */
@Component({
  selector: 'app-lease-situation-pill',
  templateUrl: './lease-situation-pill.component.html',
  styleUrls: ['./lease-situation-pill.component.scss'],
})
export class LeaseSituationPillComponent {
  private readonly http = inject(HttpClient)
  private readonly auth = inject(AuthService)
  private readonly toast = inject(MessageService)

  @Input() leaseId = ''
  @Input() situation: LeaseSituation | null = null
  @Input() situationSince: string | null = null
  /** El estado del arrendamiento. Un cancelado no entra a juicio ni se reestructura. */
  @Input() leaseStatus: string | null = null
  /**
   * La fecha de liquidación que deja la terminación anticipada del editor de saldos. No es una
   * situación que se capture: se deriva, para no tener dos verdades sobre lo mismo.
   */
  @Input() settlementDate: string | null = null
  @Output() changed = new EventEmitter<void>()

  readonly opciones = OPCIONES

  visible = false
  seleccion: LeaseSituation = 'NORMAL'
  motivo = ''
  guardando = false
  error = ''

  /** Compuerta propia: `update` la tienen también Seller y Finanzas, y esto mueve la cartera. */
  get puedeCambiar(): boolean {
    return this.auth.userHasAuthorization('leases', 'situation') && this.leaseStatus !== 'Cancelado'
  }

  /** Los arrendamientos anteriores al campo no traen situación: nadie los ha sacado de lo normal. */
  get actual(): LeaseSituation {
    return this.situation ?? 'NORMAL'
  }

  get esNormal(): boolean {
    return this.actual === 'NORMAL'
  }

  get etiqueta(): string {
    return OPCIONES.find((o) => o.value === this.actual)?.label ?? 'Normal'
  }

  /** Liquidado por terminación anticipada: se enseña igual que en la hoja de Contabilidad. */
  get terminacionAnticipada(): boolean {
    return this.leaseStatus === 'Liquidado' && !!this.settlementDate
  }

  /** El juicio y la cartera vencida se pintan en rojo; la reestructuración en ámbar. Normal, apagado. */
  get tono(): string {
    if (this.actual === 'EN_JUICIO' || this.actual === 'CARTERA_VENCIDA') return 'is-danger'
    if (this.actual === 'REESTRUCTURACION') return 'is-warn'
    return 'is-muted'
  }

  get ayuda(): string {
    return OPCIONES.find((o) => o.value === this.seleccion)?.help ?? ''
  }

  get cambia(): boolean {
    return this.seleccion !== this.actual
  }

  get motivoSuficiente(): boolean {
    return this.motivo.trim().length >= 10
  }

  get puedeGuardar(): boolean {
    return this.cambia && this.motivoSuficiente && !this.guardando
  }

  abrir(): void {
    if (!this.puedeCambiar) return
    this.seleccion = this.actual
    this.motivo = ''
    this.error = ''
    this.visible = true
  }

  guardar(): void {
    if (!this.puedeGuardar) return
    this.guardando = true
    this.error = ''

    this.http
      .patch(`${environment.apiUrl}leases/${this.leaseId}/situation`, { situation: this.seleccion, reason: this.motivo.trim() })
      .pipe(catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudo cambiar la situación', data: null })))
      .subscribe((r: any) => {
        this.guardando = false
        if (r?.error || !r?.data) {
          //* El rechazo se queda en el diálogo: el toast se va y deja el formulario abierto sin
          //* explicación, y quien lo escribió vuelve a darle a Guardar con lo mismo.
          this.error = r?.msg || 'No se pudo cambiar la situación'
          return
        }
        //* El efecto sobre la cartera se dice AQUÍ, después de aplicarlo. Es la consecuencia que no
        //* se ve en esta pantalla: el total del mes baja o sube en otra.
        const d = r.data
        const efecto = d.leavesPortfolio ? ' · sale de la cartera del mes' : d.returnsToPortfolio ? ' · vuelve a la cartera del mes' : ''
        this.toast.add({ severity: 'success', summary: 'Situación actualizada', detail: `${this.etiquetaDe(d.situation)}${efecto}`, life: 6000 })
        this.visible = false
        this.changed.emit()
      })
  }

  private etiquetaDe(s: LeaseSituation): string {
    return OPCIONES.find((o) => o.value === s)?.label ?? s
  }
}
